import { CloudUpload, Warning } from '@mui/icons-material'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { gql, useMutation, useQuery } from 'urql'

interface UserOnboardDialogProps {
  onClose: () => void
}

interface ParsedUser {
  name: string
  email: string
  role: string
  phone: string
  [key: string]: string
}

interface ValidationError {
  row: number
  message: string
}

const mutation = gql`
  mutation onboardUsers($input: [OnboardUserInput!]!) {
    onboardUsers(input: $input) {
      id
    }
  }
`

const statusQuery = gql`
  query onboardingTask($id: ID!) {
    onboardingTask(id: $id) {
      id
      status
      errorMessage
    }
  }
`

export default function UserOnboardDialog(
  props: UserOnboardDialogProps,
): JSX.Element {
  const [data, setData] = useState<ParsedUser[]>([])
  const [fileName, setFileName] = useState('')
  const [errors, setErrors] = useState<ValidationError[]>([])
  const [pollingId, setPollingId] = useState<string | null>(null)
  const [result, setResult] = useState<{
    status: 'success' | 'failure'
    message?: string
  } | null>(null)

  const [onboardState, onboardUsers] = useMutation(mutation)
  const [statusState, reexecuteQuery] = useQuery({
    query: statusQuery,
    variables: { id: pollingId },
    pause: !pollingId,
    requestPolicy: 'network-only',
  })

  // Polling logic
  useEffect(() => {
    if (!pollingId) return
    if (statusState.fetching) return

    const task = statusState.data?.onboardingTask
    if (task) {
      if (task.status === 'completed') {
        setPollingId(null)
        setResult({ status: 'success' })
      } else if (task.status === 'failed') {
        setPollingId(null)
        setResult({ status: 'failure', message: task.errorMessage })
      } else {
        // Continue polling
        const timer = setTimeout(() => {
          reexecuteQuery({ requestPolicy: 'network-only' })
        }, 1000)
        return () => clearTimeout(timer)
      }
    }
  }, [pollingId, statusState, reexecuteQuery, props])

  const { fetching: mutationFetching, error: mutationError } = onboardState
  const { error: queryError, data: queryData } = statusState

  const fetching =
    mutationFetching ||
    (!!pollingId && queryData?.onboardingTask?.status !== 'failed')
  const error =
    mutationError ||
    queryError ||
    (queryData?.onboardingTask?.status === 'failed'
      ? { message: queryData.onboardingTask.errorMessage || 'Unknown error' }
      : null)

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setFileName(file.name)
    const reader = new FileReader()
    reader.onload = (event) => {
      const text = event.target?.result as string
      parseCSV(text)
    }
    reader.readAsText(file)
  }

  const parseCSV = (text: string) => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0)
    if (lines.length === 0) return

    const headers = lines[0].split(',').map((h) => h.trim())
    const parsed = lines.slice(1).map((line) => {
      const values = line.split(',').map((v) => v.trim())
      const obj: any = {}
      headers.forEach((h, i) => {
        obj[h] = values[i] || ''
      })
      return obj as ParsedUser
    })

    validateData(parsed)
    setData(parsed)
  }

  const validateData = (users: ParsedUser[]) => {
    const errs: ValidationError[] = []
    const emailCounts = new Map<string, number>()
    const phoneCounts = new Map<string, number>()

    // Count occurrences
    users.forEach((u) => {
      if (u.email) emailCounts.set(u.email, (emailCounts.get(u.email) || 0) + 1)
      if (u.phone) phoneCounts.set(u.phone, (phoneCounts.get(u.phone) || 0) + 1)
    })

    // Identify duplicates
    users.forEach((u, index) => {
      const rowErrs: string[] = []
      if (u.email && emailCounts.get(u.email)! > 1) {
        rowErrs.push(`Duplicate email: ${u.email}`)
      }
      if (u.phone && phoneCounts.get(u.phone)! > 1) {
        rowErrs.push(`Duplicate phone: ${u.phone}`)
      }

      if (u.role && u.role.toLowerCase() === 'admin') {
        rowErrs.push(`Role 'admin' is not allowed in bulk onboarding`)
      }

      if (rowErrs.length > 0) {
        errs.push({ row: index, message: rowErrs.join(', ') })
      }
    })
    setErrors(errs)
  }

  const handleOnboard = () => {
    if (hasErrors) return

    const input = data.map((u) => ({
      name: u.name,
      email: u.email,
      role: u.role,
      phone: u.phone,
    }))

    onboardUsers({ input }).then((result) => {
      if (result.error) return
      setPollingId(result.data.onboardUsers.id)
    })
  }

  const hasErrors = errors.length > 0

  // Show "Processing..." if polling, or "Uploading..." if mutation
  const loadingText = pollingId
    ? 'Processing background task...'
    : 'Uploading...'

  return (
    <Dialog open onClose={props.onClose} maxWidth='md' fullWidth>
      <DialogTitle>Onboard Users</DialogTitle>
      <DialogContent>
        {result ? (
          <div style={{ textAlign: 'center', padding: '2em' }}>
            <Typography
              variant='h6'
              gutterBottom
              color={result.status === 'success' ? 'primary' : 'error'}
            >
              {result.status === 'success'
                ? 'Users Onboarded Successfully!'
                : 'Onboarding Failed'}
            </Typography>
            {result.message && (
              <Typography variant='body1' color='textSecondary'>
                {result.message}
              </Typography>
            )}
          </div>
        ) : fetching ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '3em',
            }}
          >
            <CircularProgress />
            <Typography variant='body2' sx={{ mt: 2 }}>
              {loadingText}
            </Typography>
          </div>
        ) : data.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2em' }}>
            <input
              type='file'
              accept='.csv'
              style={{ display: 'none' }}
              id='csv-upload-input'
              onChange={handleFileUpload}
            />
            <label htmlFor='csv-upload-input'>
              <Button
                variant='outlined'
                component='span'
                startIcon={<CloudUpload />}
              >
                Upload CSV File
              </Button>
            </label>
            <Typography variant='caption' display='block' sx={{ mt: 1 }}>
              Expected format: name,email,role (user/manager),phone
            </Typography>
          </div>
        ) : (
          <div>
            <Typography variant='subtitle1' gutterBottom>
              Preview ({data.length} users from {fileName})
            </Typography>

            {error && (
              <Alert severity='error' sx={{ mb: 2 }}>
                {error.message}
              </Alert>
            )}

            {hasErrors && (
              <Alert severity='error' sx={{ mb: 2 }}>
                Found {errors.length} validation errors. Please fix duplicate
                emails or phone numbers.
              </Alert>
            )}

            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    {data.length > 0 &&
                      Object.keys(data[0]).map((h) => (
                        <TableCell key={h}>{h}</TableCell>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row, i) => {
                    const rowError = errors.find((e) => e.row === i)
                    return (
                      <TableRow
                        key={i}
                        selected={!!rowError}
                        sx={rowError ? { bgcolor: 'error.lighter' } : {}}
                      >
                        <TableCell>
                          {rowError && (
                            <Tooltip title={rowError.message}>
                              <Warning color='error' fontSize='small' />
                            </Tooltip>
                          )}
                        </TableCell>
                        {Object.values(row).map((val, j) => (
                          <TableCell key={j}>{val}</TableCell>
                        ))}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {result ? (
          <Button onClick={props.onClose} color='primary' variant='contained'>
            Close
          </Button>
        ) : (
          <>
            <Button onClick={props.onClose} color='primary' disabled={fetching}>
              Cancel
            </Button>
            {data.length > 0 && !fetching && (
              <Button
                variant='contained'
                color='primary'
                onClick={handleOnboard}
                disabled={hasErrors}
              >
                Onboard Users
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
